import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";

import { QUERY_KEYS } from "@/model/consts/queryKeys";

import type { ErrorResponse } from "@/api/types/common.types";

import { isFinalStatus } from "@/shared/lib/exchange-status";
import { showErrorMessage } from "@/shared/lib/notify";
import { showErrorForDevelop } from "@/shared/lib/show-error-for-develop";

import { EXCHANGE_API } from "../endpoints/exchange.api";
import type { Exchange } from "../types";

/** Как часто перезапрашиваем незавершённую заявку, мс. */
const POLL_INTERVAL = 5000;

export const useGetExchange = (id: string | null, isPolling = false) => {
  // дженерик задан явно: refetchInterval читает данные собственного запроса,
  // без этого вывод типов зацикливается и data схлопывается в {}
  return useQuery<Exchange | undefined>({
    queryKey: [QUERY_KEYS.EXCHANGE.ONE_EXCHANGE, id],
    enabled: !!id,
    // статусы меняются вебхуками от процессинга, узнать о них можно только
    // опросом. Как только заявка встала в финальный статус — опрос выключается
    refetchInterval: (query) =>
      isPolling && !isFinalStatus(query.state.data?.statusExchange)
        ? POLL_INTERVAL
        : false,

    queryFn: async ({ signal }): Promise<Exchange | undefined> => {
      try {
        if (!id) {
          throw new Error("Exchange id is required");
        }
        const response = await EXCHANGE_API.getExchange({
          params: { id },
          config: { signal },
        });

        return response.data;
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          const errorMessage = (error.response?.data as ErrorResponse)?.message;
          if (axios.isCancel(error)) {
            return;
          }
          showErrorMessage(errorMessage);
          showErrorForDevelop("useGetExchange", error);
          throw error;
        } else {
          showErrorMessage("An unexpected error occurred.");
        }
      }
    },
  });
};
